import { partition } from "lodash";
import * as React from "react";

import { useUserConfig } from "@/shared/domain/config/hooks/useUserConfig";
import { useLayouts } from "@/shared/domain/layouts/hooks";
import { LayoutAccessibility } from "@/shared/domain/layouts/LayoutRecord";

import {
  groupLayoutsIntoFolders,
  type LayoutFolderGroup,
} from "./layoutFolders";

interface LayoutOptionsResult {
  personalGroups: LayoutFolderGroup[];
  teamGroups: LayoutFolderGroup[];
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Groups the org's layouts into folders, split by Personal/Team accessibility and filtered by
 * `searchQuery` (matched against layout and folder names).
 */
export function useLayoutOptions(searchQuery = ""): LayoutOptionsResult {
  const layoutsQuery = useLayouts();

  const usersDefaultLayoutQuery = useUserConfig<string>(
    "user_default_layout_id",
  );
  const defaultLayoutId = usersDefaultLayoutQuery.data ?? null;

  const { personalGroups, teamGroups } = React.useMemo(() => {
    const [personalLayouts, teamLayouts] = partition(
      layoutsQuery.data ?? [],
      (layout) => layout.accessibility === LayoutAccessibility.User,
    );

    return {
      personalGroups: groupLayoutsIntoFolders(
        personalLayouts,
        defaultLayoutId,
        searchQuery,
      ),
      teamGroups: groupLayoutsIntoFolders(
        teamLayouts,
        defaultLayoutId,
        searchQuery,
      ),
    };
  }, [layoutsQuery.data, defaultLayoutId, searchQuery]);

  return {
    personalGroups,
    teamGroups,
    isPending: layoutsQuery.isPending,
    isError: layoutsQuery.isError,
    refetch: () => {
      void layoutsQuery.refetch();
    },
  };
}
